/**
 * ResponsivePropertyGroup — Wraps any appearance control with per-breakpoint
 * responsive expansion (Desktop / Tablet / Mobile).
 *
 * Uses a render-prop pattern so the inner control doesn't know about
 * breakpoints. The wrapper handles toggle icon, breakpoint rows,
 * inheritance display, and clear buttons.
 *
 * Animation: CSS grid-template-rows 0fr → 1fr transition (200ms ease-out).
 * The breakpoint group is always mounted in the DOM for smooth animation.
 */

import { type ReactNode, useState, useId, useCallback, useEffect } from 'react'
import { Monitor, Tablet, Smartphone, X } from 'lucide-react'
import type { ResponsiveOverrides } from '@zaviago/storefront-config'
import { Label } from '@/components/ui/label'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ResponsivePropertyGroupProps {
  label: string
  propertyKey: string
  desktopValue: unknown
  responsive: ResponsiveOverrides | undefined
  onDesktopChange: (value: unknown) => void
  onResponsiveChange: (breakpoint: 'tablet' | 'mobile', value: unknown) => void
  onClearResponsive: (breakpoint: 'tablet' | 'mobile') => void
  /** Active breakpoint from DeviceToggle — highlights the matching row */
  activeBreakpoint?: 'desktop' | 'tablet' | 'mobile'
  /** Fires when user edits a breakpoint row — syncs DeviceToggle */
  onBreakpointEdit?: (breakpoint: 'desktop' | 'tablet' | 'mobile') => void
  children: (
    value: unknown,
    onChange: (v: unknown) => void,
    isInherited: boolean,
  ) => ReactNode
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const BREAKPOINT_ROWS = [
  { key: 'desktop' as const, label: 'Desktop', Icon: Monitor },
  { key: 'tablet' as const, label: 'Tablet', Icon: Tablet },
  { key: 'mobile' as const, label: 'Mobile', Icon: Smartphone },
]

function hasAnyOverride(
  responsive: ResponsiveOverrides | undefined,
  propertyKey: string,
): boolean {
  if (!responsive) return false
  const tVal = (responsive.tablet as Record<string, unknown> | undefined)?.[propertyKey]
  const mVal = (responsive.mobile as Record<string, unknown> | undefined)?.[propertyKey]
  return tVal !== undefined || mVal !== undefined
}

function getOverride(
  responsive: ResponsiveOverrides | undefined,
  breakpoint: 'tablet' | 'mobile',
  propertyKey: string,
): unknown | undefined {
  const bp = responsive?.[breakpoint] as Record<string, unknown> | undefined
  return bp?.[propertyKey]
}

function getInheritLabel(
  breakpoint: 'tablet' | 'mobile',
  responsive: ResponsiveOverrides | undefined,
  propertyKey: string,
): string {
  if (breakpoint === 'mobile') {
    const tabletOverride = getOverride(responsive, 'tablet', propertyKey)
    if (tabletOverride !== undefined) return 'Same as Tablet'
  }
  return 'Same as Desktop'
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ResponsivePropertyGroup({
  label,
  propertyKey,
  desktopValue,
  responsive,
  onDesktopChange,
  onResponsiveChange,
  onClearResponsive,
  activeBreakpoint,
  onBreakpointEdit,
  children,
}: ResponsivePropertyGroupProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const groupId = useId()

  const hasOverrides = hasAnyOverride(responsive, propertyKey)

  // Auto-collapse when all overrides are cleared
  useEffect(() => {
    if (isExpanded && !hasOverrides) {
      setIsExpanded(false)
    }
  }, [hasOverrides]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = useCallback(() => {
    setIsExpanded((prev) => !prev)
  }, [])

  // Resolve effective value for a breakpoint (inheritance cascade)
  const getEffectiveValue = useCallback(
    (breakpoint: 'tablet' | 'mobile'): unknown => {
      if (breakpoint === 'tablet') {
        return getOverride(responsive, 'tablet', propertyKey) ?? desktopValue
      }
      // Mobile: mobile override → tablet override → desktop
      return (
        getOverride(responsive, 'mobile', propertyKey) ??
        getOverride(responsive, 'tablet', propertyKey) ??
        desktopValue
      )
    },
    [responsive, propertyKey, desktopValue],
  )

  // Blue dot: visible when collapsed AND has overrides
  const showDot = hasOverrides && !isExpanded

  return (
    <div className="space-y-1.5">
      {/* Label row + toggle icon — always visible */}
      <div className="flex items-center gap-1.5">
        <Label className="text-[13px] font-medium text-gray-600">{label}</Label>
        <button
          type="button"
          onClick={handleToggle}
          aria-expanded={isExpanded}
          aria-controls={groupId}
          className={`relative ml-auto rounded-md p-0.5 ${
            isExpanded
              ? 'text-sky-500 hover:text-sky-600'
              : 'text-gray-300 hover:text-gray-500'
          }`}
          title={isExpanded ? 'Collapse responsive controls' : 'Toggle responsive controls'}
        >
          <Monitor className="h-3.5 w-3.5" />
          <span
            className={`absolute -bottom-0.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-sky-500 transition-opacity duration-200 ${
              showDot ? 'opacity-100' : 'opacity-0'
            }`}
          />
        </button>
      </div>

      {/* Flat desktop control — visible only when collapsed */}
      {!isExpanded && children(desktopValue, onDesktopChange, false)}

      {/* Breakpoint group — always mounted, animated via grid-template-rows */}
      <div
        id={groupId}
        role="group"
        aria-label={`Responsive ${label} controls`}
        aria-hidden={!isExpanded}
        className="grid transition-[grid-template-rows] duration-200 ease-out"
        style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div className="rounded-sm bg-gray-50 p-2">
            {BREAKPOINT_ROWS.map(({ key: bp, label: bpLabel, Icon }, idx) => {
              const isDesktop = bp === 'desktop'
              const isLast = idx === BREAKPOINT_ROWS.length - 1
              const override = isDesktop ? undefined : getOverride(responsive, bp, propertyKey)
              const hasOverride = override !== undefined
              const isActive = bp === activeBreakpoint

              return (
                <div
                  key={bp}
                  className={`flex items-start gap-2 rounded-xs py-2 ${!isLast ? 'border-b border-gray-100' : ''} ${
                    isActive ? 'bg-sky-50/50 ring-1 ring-sky-100' : ''
                  }`}
                >
                  {/* Device label */}
                  <div className="flex w-[72px] shrink-0 items-center gap-1.5 pl-1 pt-0.5">
                    <Icon className="h-3 w-3 text-gray-400" aria-hidden="true" />
                    <span className="text-[11px] font-medium text-gray-500">{bpLabel}</span>
                  </div>

                  {/* Control or inherit placeholder */}
                  <div className="min-w-0 flex-1">
                    {isDesktop ? (
                      children(desktopValue, (v) => {
                        onBreakpointEdit?.('desktop')
                        onDesktopChange(v)
                      }, false)
                    ) : hasOverride ? (
                      children(
                        override,
                        (v) => {
                          onBreakpointEdit?.(bp)
                          onResponsiveChange(bp, v)
                        },
                        false,
                      )
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          onBreakpointEdit?.(bp)
                          onResponsiveChange(bp, getEffectiveValue(bp))
                        }}
                        className="py-1 text-xs italic text-gray-300 hover:text-gray-400"
                        aria-label={`Set ${bpLabel.toLowerCase()} ${label.toLowerCase()} override (currently inherits from ${
                          bp === 'mobile' && getOverride(responsive, 'tablet', propertyKey) !== undefined
                            ? 'tablet'
                            : 'desktop'
                        })`}
                      >
                        {getInheritLabel(bp, responsive, propertyKey)}
                      </button>
                    )}
                  </div>

                  {/* Clear button (only for non-desktop overridden rows) */}
                  {!isDesktop && hasOverride && (
                    <button
                      type="button"
                      onClick={() => onClearResponsive(bp)}
                      aria-label={`Clear ${bpLabel.toLowerCase()} ${label.toLowerCase()} override`}
                      className="mt-0.5 shrink-0 rounded-md p-0.5 text-gray-300 hover:bg-gray-100 hover:text-gray-500"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
